import { Avatar, IconButton, Logout } from '@taskall/react';
import { cx } from '@taskall/ui';
import { NavLink } from 'react-router-dom';
import { Brand } from '@/components/Brand';
import { EnvBadge } from '@/components/EnvBadge';
import { useAuth } from '@/features/auth/AuthProvider';
import { initials } from '@/lib/format';
import { NAV_ITEMS, PLATFORM_NAV_ITEMS, type NavItem } from './nav';

export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { session, can, logout } = useAuth();
  const mySchool = !session?.platformAdmin && session?.school && session.memberships.length <= 1 ? session.school : null;
  // Gestão de uma única escola: "Minha escola" abre direto os detalhes dela.
  const items = NAV_ITEMS.filter((i) => can(i.permission)).map((i) =>
    i.to === '/escolas' && mySchool ? { ...i, to: `/escolas/${mySchool.id}`, label: 'Minha escola' } : i,
  );
  const platformItems = PLATFORM_NAV_ITEMS.filter((i) => can(i.permission));
  return (
    <div className="flex h-full flex-col px-6 pb-6 pt-6">
      <div className="mb-8 px-0.5">
        <Brand size="md" className="h-[56px] max-w-full" />
        <EnvBadge className="mt-3" />
      </div>
      <nav aria-label="Navegação principal" className="flex-1">
        <NavList items={items} onNavigate={onNavigate} />
        {platformItems.length > 0 ? (
          <>
            <p className="mb-2 mt-6 px-4 text-xs font-semibold uppercase tracking-wide text-[var(--taskall-content-tertiary)]" id="nav-plataforma">
              Plataforma
            </p>
            <NavList items={platformItems} onNavigate={onNavigate} labelledBy="nav-plataforma" />
          </>
        ) : null}
        <div className="mt-5 border-t border-[var(--taskall-border-subtle)]" />
      </nav>
      {session ? (
        <div className="mt-6 border-t border-[var(--taskall-border-subtle)] pt-5">
          <div className="flex items-center gap-3">
            <Avatar size="md" fallback={initials(session.profile.displayName)} alt="" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[15px] font-medium text-[var(--taskall-content-primary)]">{session.profile.displayName}</p>
              <p className="truncate text-[13px] text-[var(--taskall-content-tertiary)]">{session.roleLabel}</p>
            </div>
            <IconButton icon={Logout} aria-label="Sair da plataforma" size="small" tone="neutral" visualStyle="light" onClick={() => void logout()} />
          </div>
          <NavLink to="/alterar-senha" onClick={onNavigate} className="mt-3 inline-block text-[13px] text-[var(--taskall-brand)] hover:underline">
            Alterar senha
          </NavLink>
          <p className="mt-2 truncate text-xs text-[var(--taskall-content-tertiary)]" title={session.school?.name ?? 'Equipe TaskAll'}>
            {session.school ? `${session.school.name} • ${session.school.code}` : 'Equipe TaskAll • nenhuma escola cadastrada'}
          </p>
          {session.school && (session.platformAdmin || session.memberships.length > 1) ? (
            <NavLink to="/escolas" onClick={onNavigate} className="mt-1 inline-block text-[13px] text-[var(--taskall-brand)] hover:underline">
              Trocar escola
            </NavLink>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function NavList({ items, onNavigate, labelledBy }: { items: NavItem[]; onNavigate?: () => void; labelledBy?: string }) {
  return (
    <ul className="space-y-1.5" aria-labelledby={labelledBy}>
      {items.map((item) => (
        <li key={item.to}>
          <NavLink
            to={item.to}
            end={item.to === '/plataforma'}
            onClick={onNavigate}
            className={({ isActive }) =>
              cx(
                'flex h-11 items-center gap-3 rounded-[var(--taskall-radius-3)] px-4 text-[15px] transition-colors',
                isActive
                  ? 'bg-[var(--taskall-brand-soft)] font-medium text-[var(--taskall-brand)]'
                  : 'text-[var(--taskall-content-primary)] hover:bg-[var(--taskall-background-hover)]',
              )
            }
          >
            <item.icon size={20} aria-hidden="true" />
            {item.label}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}
